import React, { useState, useEffect } from 'react';
import {
  Form,
  Input,
  Button,
  Typography,
  Space,
  Select,
  Switch,
  Popconfirm,
  Tag,
  message,
} from 'antd';
import Table from 'components/Common/Table';
import {
  ArrowLeftOutlined,
  ArrowRightOutlined,
  PlusOutlined,
  DeleteOutlined,
  UserOutlined,
} from '@ant-design/icons';
import * as actions from 'store/actions/AuthActions';
import { RECIPIENT_COLORS } from '../DocumentWorkflow';

const { Title, Text } = Typography;
const { Option } = Select;

const RECIPIENT_ROLES = [
  { value: 'SIGNER', label: 'Signer', description: 'Must sign the document' },
  { value: 'APPROVER', label: 'Approver', description: 'Must approve before others can sign' },
  { value: 'VIEWER', label: 'Viewer', description: 'Can view the document only' },
  { value: 'CC', label: 'CC', description: 'Receives a copy when complete' },
];

const StepAddSigners = ({
  formData,
  onNext,
  onBack,
  onSubmit,
  setFormData,
  documentId,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [recipients, setRecipients] = useState([]);
  const [signingOrder, setSigningOrder] = useState(false);
  const [localChanges, setLocalChanges] = useState(false);

  useEffect(() => {
    // Only sync from formData if no local changes pending
    if (!localChanges && formData.recipients && Array.isArray(formData.recipients)) {
      setRecipients(
        formData.recipients.map((r, idx) => ({
          ...r,
          key: r.id || `temp_${idx}`,
          color: r.color || RECIPIENT_COLORS[idx % RECIPIENT_COLORS.length],
        }))
      );
    }
    if (formData.signing_order !== undefined) {
      setSigningOrder(formData.signing_order);
    }
  }, [formData, localChanges]);

  const handleAddRecipient = async () => {
    try {
      const values = await form.validateFields();
      const newRecipient = {
        id: `temp_${Date.now()}`,
        key: `temp_${Date.now()}`,
        email: values.email,
        name: values.name,
        recipient_role: values.recipient_role || 'SIGNER',
        order: recipients.length + 1,
        color: RECIPIENT_COLORS[recipients.length % RECIPIENT_COLORS.length],
      };

      setLocalChanges(true);
      setRecipients([...recipients, newRecipient]);
      form.resetFields();
      message.success('Recipient added');
    } catch (err) {
      console.error('Validation failed:', err);
    }
  };

  const handleRemoveRecipient = async (recipient) => {
    try {
      // If it's a saved recipient (has a real ID), delete from backend
      if (recipient.id && !recipient.id.toString().startsWith('temp_')) {
        await actions.removeDocumentRecipient(documentId, recipient.id);
      }
      // Only update state AFTER successful API call
      setLocalChanges(true);
      setRecipients((prev) => prev.filter((r) => r.key !== recipient.key));
      message.success('Recipient removed');
    } catch (err) {
      console.error('Error removing recipient:', err);
      message.error('Failed to remove recipient');
      // State NOT updated - recipient stays in list
    }
  };

  const handleRoleChange = (key, role) => {
    setRecipients(
      recipients.map((r) =>
        r.key === key ? { ...r, recipient_role: role } : r
      )
    );
  };

  const handleFinish = async () => {
    if (recipients.length === 0) {
      message.warning('Please add at least one recipient');
      return;
    }

    setLoading(true);
    try {
      // Update form data with recipients
      setFormData((prev) => ({
        ...prev,
        recipients,
        signing_order: signingOrder,
      }));

      await onSubmit(2, {
        recipients: recipients.map((r, idx) => ({
          id: r.id && !r.id.toString().startsWith('temp_') ? r.id : undefined,
          email: r.email,
          name: r.name,
          recipient_role: r.recipient_role,
          order: signingOrder ? idx + 1 : null,
        })),
        signing_order: signingOrder,
      });
      // Reset local changes flag after successful submit
      setLocalChanges(false);
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    {
      title: '',
      dataIndex: 'color',
      key: 'color',
      width: 30,
      render: (color) => (
        <div
          style={{
            width: 16,
            height: 16,
            borderRadius: '50%',
            backgroundColor: color,
          }}
        />
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <Space>
          <UserOutlined />
          {name}
        </Space>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Role',
      dataIndex: 'recipient_role',
      key: 'recipient_role',
      render: (role, record) => (
        <Select
          value={role}
          onChange={(value) => handleRoleChange(record.key, value)}
          style={{ width: 120 }}
          size="small"
        >
          {RECIPIENT_ROLES.map((r) => (
            <Option key={r.value} value={r.value}>
              {r.label}
            </Option>
          ))}
        </Select>
      ),
    },
    {
      title: signingOrder ? 'Order' : '',
      dataIndex: 'order',
      key: 'order',
      width: 60,
      render: (order, record, index) =>
        signingOrder ? (
          <Tag color="blue">{index + 1}</Tag>
        ) : null,
    },
    {
      title: '',
      key: 'action',
      width: 50,
      render: (_, record) => (
        <Popconfirm
          title="Remove this recipient?"
          onConfirm={() => handleRemoveRecipient(record)}
          okText="Yes"
          cancelText="No"
        >
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            size="small"
          />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div>
      <Title level={4}>Add Recipients</Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        Add the people who need to sign, approve, or receive this document.
      </Text>

      {/* Add Recipient Form */}
      <Form form={form} layout="vertical">
        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, message: 'Please enter recipient name' }]}
        >
          <Input placeholder="Enter name" />
        </Form.Item>

        <Form.Item
          name="email"
          label="Email"
          rules={[
            { required: true, message: 'Please enter email' },
            { type: 'email', message: 'Please enter a valid email' },
          ]}
        >
          <Input placeholder="Enter email address" />
        </Form.Item>

        <Form.Item name="recipient_role" label="Role" initialValue="SIGNER">
          <Select optionLabelProp="label">
            {RECIPIENT_ROLES.map((r) => (
              <Option key={r.value} value={r.value} label={r.label}>
                <Space direction="vertical" size={0}>
                  <span>{r.label}</span>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {r.description}
                  </Text>
                </Space>
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Button
          type="dashed"
          onClick={handleAddRecipient}
          icon={<PlusOutlined />}
          style={{ width: '100%', marginBottom: 16 }}
        >
          Add Recipient
        </Button>
      </Form>

      {/* Recipients Table */}
      {recipients.length > 0 && (
        <>
          <div style={{ marginBottom: 16 }}>
            <Space>
              <Switch
                checked={signingOrder}
                onChange={setSigningOrder}
                size="small"
              />
              <Text>Enable signing order</Text>
            </Space>
            {signingOrder && (
              <Text type="secondary" style={{ display: 'block', marginTop: 4 }}>
                Recipients will sign in the order shown below
              </Text>
            )}
          </div>

          <Table
            dataSource={recipients}
            columns={columns}
            pagination={false}
            size="small"
            rowKey="key"
            style={{ marginBottom: 24 }}
          />
        </>
      )}

      {/* Navigation Buttons */}
      <Space style={{ marginTop: 24 }}>
        <Button onClick={onBack} icon={<ArrowLeftOutlined />} size="large">
          Back
        </Button>
        <Button
          type="primary"
          onClick={handleFinish}
          loading={loading}
          icon={<ArrowRightOutlined />}
          size="large"
          disabled={recipients.length === 0}
        >
          Next: Add Fields
        </Button>
      </Space>
    </div>
  );
};

export default StepAddSigners;
